/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transform hover:scale-105 active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-gradient-joy text-white hover:opacity-90 shadow-joy font-semibold",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg",
        outline:
          "border-2 border-joy/30 bg-background hover:bg-joy/10 hover:border-joy/60 text-joy transition-all hover:shadow-joy/20",
        secondary:
          "bg-gradient-calm text-white hover:opacity-90 shadow-calm font-semibold",
        ghost: "hover:bg-joy/10 hover:text-joy transition-all",
        link: "text-joy underline-offset-4 hover:underline hover:text-joy-secondary font-medium",
        // Boutons thématiques selon vos directives
        health: "bg-gradient-health text-white hover:opacity-90 shadow-health font-semibold",
        joy: "bg-gradient-joy text-white hover:opacity-90 shadow-joy font-semibold",
        calm: "bg-gradient-calm text-white hover:opacity-90 shadow-calm font-semibold", 
        warm: "bg-gradient-warm text-white hover:opacity-90 shadow-warm font-semibold",
        futuristic: "bg-gradient-futuristic text-foreground hover:opacity-95 shadow-futuristic font-semibold border border-white/10",
      
      },
      size: {
        default: "h-10 sm:h-12 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base",
        sm: "h-9 sm:h-11 rounded-lg px-3 sm:px-4 text-xs sm:text-sm",
        lg: "h-12 sm:h-14 rounded-xl px-6 sm:px-8 text-base sm:text-lg font-semibold",
        icon: "h-10 w-10 sm:h-12 sm:w-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
